/**
 * Feed hooks — infinite scroll for For You and Following tabs.
 * Cursor-based pagination with TanStack Query useInfiniteQuery.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { fetchForYouFeed, fetchFollowingFeed, fetchFollowingSets, fetchUserPosts } from '@turnifly/shared/api'
import type { FeedItem } from '@turnifly/shared/types'
import type { FollowSets } from '@turnifly/shared/api'

export const feedKeys = {
  forYou: (userId: string) => ['feed', 'for-you', userId] as const,
  following: (userId: string) => ['feed', 'following', userId] as const,
  comments: (postId: string) => ['feed', 'comments', postId] as const,
  quickComments: ['feed', 'quick-comments'] as const,
  followingIds: (userId: string) => ['feed', 'following-ids', userId] as const,
  search: (query: string) => ['feed', 'search', query] as const,
} as const

export function useForYouFeed(userId: string | undefined) {
  return useInfiniteQuery<FeedItem[]>({
    queryKey: feedKeys.forYou(userId ?? ''),
    queryFn: ({ pageParam }) =>
      fetchForYouFeed(userId!, pageParam as number | null, 20),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined
      const last = lastPage[lastPage.length - 1]
      return last?.score ?? undefined
    },
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function useFollowingFeed(userId: string | undefined) {
  return useInfiniteQuery<FeedItem[]>({
    queryKey: feedKeys.following(userId ?? ''),
    queryFn: ({ pageParam }) =>
      fetchFollowingFeed(userId!, pageParam as string | null, 20),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined
      const last = lastPage[lastPage.length - 1]
      return last?.created_at ?? null
    },
    enabled: !!userId,
    staleTime: 60_000,
  })
}

/**
 * User's posts for profile lenta tab.
 */
export function useUserPosts(targetUserId: string | undefined, currentUserId: string | undefined) {
  return useInfiniteQuery<FeedItem[]>({
    queryKey: ['profile-feed', targetUserId ?? ''],
    queryFn: ({ pageParam }) =>
      fetchUserPosts(targetUserId!, currentUserId!, pageParam as string | null, 20),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined
      const last = lastPage[lastPage.length - 1]
      return last?.created_at ?? null
    },
    enabled: !!targetUserId && !!currentUserId,
    staleTime: 60_000,
  })
}

/**
 * Returns accepted + pending follow sets.
 * Used to show follow/pending/following state in feed & search.
 */
export function useFollowingSets(userId: string | undefined) {
  return useQuery<FollowSets>({
    queryKey: feedKeys.followingIds(userId ?? ''),
    queryFn: () => fetchFollowingSets(userId!),
    enabled: !!userId,
    staleTime: 120_000,
  })
}
